'use client';

import { useCallback, useEffect, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from '@medizzconnect/shared';

const overlayClass = [
  'fixed inset-0 z-modal bg-black/50',
  'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
].join(' ');

const contentVariants = cva(
  [
    'fixed z-modal bg-white rounded-2xl shadow-2xl',
    'focus:outline-none',
    'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
    'max-h-[85dvh] overflow-y-auto',
    // Mobile: Fullscreen-Bottom-Sheet
    'bottom-0 left-0 right-0 rounded-b-none',
    // Desktop: Zentriert
    'sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:right-auto sm:rounded-2xl',
  ],
  {
    variants: {
      size: {
        sm: 'sm:max-w-sm sm:w-full',
        md: 'sm:max-w-md sm:w-full',
        lg: 'sm:max-w-lg sm:w-full',
        xl: 'sm:max-w-xl sm:w-full',
        full: 'sm:max-w-3xl sm:w-full',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

interface ModalProps extends VariantProps<typeof contentVariants> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export function Modal({ open, onOpenChange, title, description, children, size, className }: ModalProps) {
  // ESC schließt Modal
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, handleEscape]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlayClass} />
        <Dialog.Content className={cn(contentVariants({ size }), className)}>
          {/* Drag Handle für Mobile */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="h-1 w-10 rounded-full bg-border-default" />
          </div>

          <div className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <Dialog.Title className="text-lg font-semibold text-text-primary">
                  {title}
                </Dialog.Title>
                {description && (
                  <Dialog.Description className="mt-1 text-sm text-text-secondary">
                    {description}
                  </Dialog.Description>
                )}
              </div>
              <Dialog.Close
                className={cn(
                  'rounded-lg p-2 text-text-muted',
                  'hover:bg-bg-subtle hover:text-text-primary',
                  'transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                )}
                aria-label="Schließen"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </Dialog.Close>
            </div>

            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
